import React, { type ReactElement } from 'react';
import { useFormContext } from 'react-hook-form';
import Select from 'atom-components/form-fields-for-hook-form/select';
import Label from 'atom-components/label';
import { defaultPage, pageSizes } from 'helpers/pagination.helpers';
import styles from './page-size.module.scss';

type PageSizeProps = {
  triggerFormSubmission: () => void;
};

export default function PageSize({ triggerFormSubmission }: PageSizeProps): ReactElement {
  const { control, setValue } = useFormContext();
  const selectName = 'pageSize';
  const selectLabel = 'Page Size';

  return (
    <div className={styles.pageSize}>
      <Label htmlFor={selectName} className={styles.pageSizeLabel}>
        {selectLabel}
      </Label>
      <div className={styles.pageSizeSelectContainer}>
        <Select
          id={selectName}
          control={control}
          name={selectName}
          ariaLabel={selectLabel}
          onChange={(): void => {
            /**
             * Reset the page number back to 1 so that it doesn't interfere with
             * the page size. For example, if the user increases the page size,
             * the current page that the user is on or could navigate to by
             * clicking Next/Last may not longer exist because the page sizes are
             * now larger. It keeps things simple to just reset the current page
             * number rather than attempting to recalcuate these.
             *
             * Note, resetField cannot be used here both for technical reasons (it
             * won't be triggered in time before the form is submitted) and
             * because the default value of page number is not always 1.
             */
            setValue('page', defaultPage);

            /**
             * Trigger a form submission so that the new page size is applied
             * straight away.
             */
            triggerFormSubmission();
          }}
        >
          {pageSizes.map((pageSize) => (
            <Select.Option
              key={pageSize.value}
              value={pageSize.value.toString()}
              id={pageSize.value.toString()}
            >
              {pageSize.label}
            </Select.Option>
          ))}
        </Select>
      </div>
    </div>
  );
}
